import { useId, useState, type KeyboardEvent } from 'react';
import { buText, nurMitHinweis } from './kontenliste';
import { buErlaubt, kontoInfo } from './logik';
import type { KontoListe } from './types';

// St-Sl zum Gegenkonto: tippbar (9, 8, leer), Klartext daneben. Nur die für das Konto erlaubten Schlüssel;
// Automatikkonten rechnen die Steuer selbst („rechnet automatisch“), dann gibt es kein Feld. Auf Konten, die meist
// ohne Vorsteuer gebucht werden (4970, 4380), sind 9 und 8 eintragbar, mit dem Zusatz „meist ohne“.
export function StSlFeld({
  value,
  onChange,
  gegenkonto,
  liste,
  label,
  feld,
  disabled,
  onEnter,
}: {
  value: string;
  onChange(v: string): void;
  gegenkonto: string;
  liste: KontoListe | undefined;
  label: string;
  feld: string;
  disabled?: boolean;
  onEnter(): void;
}) {
  const id = useId();
  const [fokus, setFokus] = useState(false);
  const info = kontoInfo(liste, gegenkonto.trim());
  const bu = value.trim();

  // kein Schlüssel möglich; steht trotzdem einer da (Vorschlag), bleibt das Feld zum Löschen sichtbar
  // (und solange der Cursor darin steht, damit Enter weitergeht)
  if ((info.automatik || (info.erlaubt && info.erlaubt.length === 0)) && !bu && !fokus) {
    return (
      <div className="sf">
        <span className="sf-l" id={`${id}-l`}>
          {label}
        </span>
        <span className="sf-fix" aria-labelledby={`${id}-l`}>
          {info.automatik ? 'rechnet automatisch' : 'kein St-Sl'}
        </span>
      </div>
    );
  }

  const erlaubt = info.erlaubt ?? liste?.bu.map((b) => b.schluessel) ?? [];
  const ok = buErlaubt(liste, gegenkonto.trim(), bu);
  const text = bu ? `${buText(liste, bu) ?? ''}${nurMitHinweis(gegenkonto.trim(), bu) ? ' (meist ohne)' : ''}` : 'ohne';
  const taste = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      onEnter();
    }
  };
  return (
    <div className="sf">
      <label className="sf-l" htmlFor={`${id}-i`}>
        {label}
      </label>
      <span className="sf-r">
        <input
          id={`${id}-i`}
          className="num"
          value={value}
          disabled={disabled}
          data-feld={feld}
          data-enter=""
          inputMode="numeric"
          autoComplete="off"
          enterKeyHint="next"
          maxLength={3}
          aria-invalid={!ok || undefined}
          aria-describedby={`${id}-t`}
          onFocus={(e) => {
            e.target.select();
            setFokus(true);
          }}
          onBlur={() => setFokus(false)}
          onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))}
          onKeyDown={taste}
        />
        <span id={`${id}-t`} className={`sf-t${ok ? '' : ' bad'}`}>
          {ok ? text : info.automatik ? 'nicht erlaubt, rechnet automatisch' : 'nicht erlaubt'}
        </span>
      </span>
      {erlaubt.length > 0 && erlaubt.length <= 4 && (
        <span className="sf-opt" role="group" aria-label={`${label}: erlaubt`}>
          {['', ...erlaubt].map((k) => (
            <button
              key={k || 'leer'}
              type="button"
              tabIndex={-1}
              disabled={disabled}
              aria-pressed={bu === k}
              title={k ? (buText(liste, k) ?? k) : 'ohne St-Sl'}
              onClick={() => onChange(k)}
            >
              {k || 'leer'}
            </button>
          ))}
        </span>
      )}
    </div>
  );
}
